import React, { useEffect, useState } from 'react';
import { UiKit } from '@datavant/uikit';
import { useToast, VStack } from '@chakra-ui/react';
import { trpc, tRPCProxyClient } from '../../../utils/trpc';
import useUser from '../../../lib/useUser';
import { sendDocusignSlackAlert } from './helpers';

type DocuSignClick = {
    Clickwrap: {
        render: (
            options: {
                agreementUrl: string;
                onAgreed?: () => void;
            },
            selector: string
        ) => void;
    };
};

export const AgreementModal = (props: {
    onClose: () => void;
    setIsNextStepsModalOpen: (isOpen: boolean) => void;
}) => {
    const currentUser = useUser().user;
    const toast = useToast();
    const [isRendering, setIsRendering] = useState(true);
    let observer: MutationObserver;
    const { data: agreementData } = trpc.autofulfill.getAutofulfillAgreement.useQuery(undefined, {
        refetchOnWindowFocus: false,
        enabled: currentUser?.uuid !== undefined,
    });

    useEffect(() => {
        const container = document.querySelector('#ds-clickwrap');
        if (agreementData?.status === 'agreed') {
            setIsRendering(false);
        } else if (agreementData?.agreementUrl && container) {
            try {
                // Check if the iframe has loaded its contents
                // Otherwise there is a confusing delay for users
                observer = new MutationObserver(() => {
                    const iframe = container.querySelector('iframe');

                    if (iframe && !iframe.dataset.hasListener) {
                        iframe.dataset.hasListener = 'true';
                        // When iframe fully loads, hide spinner
                        iframe.addEventListener('load', () => {
                            setIsRendering(false);
                        });
                    }
                });

                observer.observe(container, { childList: true, subtree: true });

                const docuSignClick = (window as any).docuSignClick as DocuSignClick;
                docuSignClick.Clickwrap.render(
                    {
                        agreementUrl: agreementData?.agreementUrl,
                        onAgreed: onAgreed,
                    },
                    '#ds-clickwrap'
                );
            } catch (error) {
                console.error('Error rendering Clickwrap:', error);
                setIsRendering(false);
            }
        }

        return () => {
            if (observer) observer.disconnect();
        };
    }, [agreementData]);

    const onAgreed = async () => {
        if (agreementData?.agreementId) {
            props.setIsNextStepsModalOpen(true);
            props.onClose();
            createAgreement({
                agreementId: agreementData.agreementId,
            });
            void sendDocusignSlackAlert(agreementData.agreementId);
        }
    };

    const { mutate: createAgreement, isLoading: isMutating } =
        trpc.autofulfill.createDigitalAgreement.useMutation({
            onSuccess: async () => {
                toast({
                    title: 'Agreement submitted. Please refer to the onboarding tracker on the left sidebar for next steps.',
                    status: 'success',
                    duration: 2000,
                    isClosable: true,
                });
            },
            onError: async () => {
                toast({
                    title: 'There was an error submitting this agreement. Please try again or reach out to customer service via help.',
                    status: 'error',
                    duration: 2000,
                    isClosable: true,
                });
            },
        });

    const ModalBody = (
        <VStack width={'100%'} alignItems={'left'}>
            <div id="ds-clickwrap" style={{ width: '100%' }} />
            {!agreementData || isRendering ? (
                <UiKit.Spinner />
            ) : (
                agreementData.status === 'agreed' && (
                    <UiKit.Text.BodyRegular>
                        You have already completed the Autofulfill Agreement. Please refer to the onboarding
                        tracker located at the left sidebar for next steps.
                    </UiKit.Text.BodyRegular>
                )
            )}
        </VStack>
    );

    return (
        <UiKit.Modal
            title={'Agreement for Digital Fulfillment and Provider Agreement'}
            bodyFragment={ModalBody}
            footerFragment={<></>}
            onClose={props.onClose}
            isOpen={true} // always open bc it is conditionally rendered on tracker side
            size={'6xl'}
            fullScreen={false}
        />
    );
};
